import { Helmet } from 'react-helmet';

/**
 * SEOMetaTags component for managing dynamic meta tags and structured data
 * Now uses React Helmet for better SSR/pre-rendering support
 * 
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Meta description
 * @param {string} props.keywords - Meta keywords
 * @param {string} props.canonicalUrl - Canonical URL
 * @param {Object|Array} props.structuredData - JSON-LD structured data object or array of objects
 * @param {string} props.robots - Optional robots meta (default: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1")
 * @returns {React.Component} - React Helmet component that renders meta tags in head
 */
const SEOMetaTags = ({ title, description, keywords, canonicalUrl, structuredData, image, robots }) => {
  // Handle both single object and array of structured data
  const dataArray = structuredData ? (Array.isArray(structuredData) ? structuredData : [structuredData]) : [];
  
  // Strip URL parameters from canonical URL to prevent duplicate content
  // Keep only the clean path without query parameters
  const cleanCanonicalUrl = canonicalUrl ? canonicalUrl.split('?')[0].split('#')[0] : canonicalUrl;
  
  // Default robots directive for normal pages
  const robotsContent = robots || "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
  
  return (
    <Helmet>
      {/* Page Title */}
      <title>{title}</title>
      
      {/* Standard Meta Tags */}
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content="Temp2Doc" />
      <meta name="robots" content={robotsContent} />
      
      {/* Open Graph Tags */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Temp2Doc" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={cleanCanonicalUrl} />
      <meta property="og:locale" content="en_US" />
      {image && <meta property="og:image" content={image} />}
      {image && <meta property="og:image:width" content="1200" />}
      {image && <meta property="og:image:height" content="630" />}
      
      {/* Canonical URL - always clean without parameters */}
      <link rel="canonical" href={cleanCanonicalUrl} />
      
      {/* Additional SEO Tags */}
      <meta name="format-detection" content="telephone=no" />
      <meta httpEquiv="x-ua-compatible" content="ie=edge" />
      
      {/* Structured Data (JSON-LD) */}
      {dataArray.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOMetaTags;
